import {
  type FlowAuditQuery,
  type FlowDefinitionHistoryFilters,
  type FlowKind,
  type FlowPricingMode,
  type FlowRunBlockDataVariant,
} from 'flow-schema';

import { type Common } from '../common.js';
import { type Audit } from '../models/flow/audit.js';
import {
  type CodeMirrorCompletionSnippet,
  type CreateFlowWithDefinitionValues,
  type FlowData,
  type FlowDefinitionFindAllOptions,
  type FlowDefinitionForList,
  type FlowDefinitionWithFlow,
  type FlowTag,
  type UpdateValuesWithFlow,
} from '../models/flow/flowDefinition.js';
import type * as FlowDefinitionCostEstimate from '../models/flow/flowDefinitionCostEstimate.js';
import {
  type FlowDefinitionBuiltHistory,
  type FlowDefinitionHistoryWithUser,
  type HumanReadableHistory,
} from '../models/flow/flowDefinitionHistory.js';
import { type FlowRunWithData } from '../models/flow/flowRun.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/flow-definitions';

  constructor(common: Common) {
    this.common = common;
  }

  async create(values: CreateFlowWithDefinitionValues): Promise<FlowDefinitionWithFlow> {
    const url = this.common.constructPath(this.baseEndpoint);
    const flow = await this.common.postJSON(url, values);
    return flow;
  }

  async findAll(queryParams?: FlowDefinitionFindAllOptions): Promise<FlowDefinitionForList[]> {
    const url = this.common.constructPath(this.baseEndpoint, queryParams);
    return await this.common.getJSON(url);
  }

  async update(
    flowDefinitionId: string,
    values: UpdateValuesWithFlow
  ): Promise<FlowDefinitionWithFlow> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowDefinitionId}`);
    return await this.common.patchJSON(url, values);
  }

  async findOneById(flowDefinitionId: string): Promise<FlowDefinitionWithFlow> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowDefinitionId}`);
    return await this.common.getJSON(url);
  }

  async clone(flowDefinitionId: string): Promise<FlowDefinitionWithFlow> {
    const url = this.common.constructPath(`${this.baseEndpoint}/clone/${flowDefinitionId}`);
    return await this.common.postJSON(url, {});
  }

  async cloneAsNewFlow(args: {
    flowDefinitionId: string;
    values: {
      name: string;
      description?: string;
      agentId?: string | null;
      organizationId?: string;
      documentationMarkdown?: string | null;
      tags?: FlowTag[] | null;
      pricingMode?: FlowPricingMode;
    };
    targetFlowKind: FlowKind;
  }): Promise<FlowDefinitionWithFlow> {
    const { flowDefinitionId, values, targetFlowKind } = args;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${flowDefinitionId}/clone-as-new-flow`
    );
    return await this.common.postJSON(url, {
      values,
      duplicateNameStrategy: 'AppendSuffix',
      targetFlowKind,
    });
  }

  async findAuditTrail(flowDefinitionId: string, query?: FlowAuditQuery): Promise<Audit[]> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${flowDefinitionId}/audit-trail`,
      query
    );
    return await this.common.getJSON(url);
  }

  async postCostEstimate(
    flowDefinitionId: string,
    values: FlowDefinitionCostEstimate.CostEstimateRequestParams
  ): Promise<FlowDefinitionCostEstimate.CostEstimateResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowDefinitionId}/cost-estimate`);
    return await this.common.postJSON(url, values);
  }

  async findHistory(
    flowDefinitionId: string,
    query?: FlowDefinitionHistoryFilters
  ): Promise<FlowDefinitionHistoryWithUser> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${flowDefinitionId}/history`,
      query
    );
    return await this.common.getJSON(url);
  }

  async findHumanReadableHistory(
    flowDefinitionId: string,
    query?: FlowDefinitionHistoryFilters
  ): Promise<HumanReadableHistory[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowDefinitionId}/history`, {
      humanReadable: true,
      ...query,
    });
    return await this.common.getJSON(url);
  }

  async buildHistory(args: {
    flowDefinitionId: string;
    newFlowData: FlowData;
    baseFlowData?: FlowData;
  }): Promise<FlowDefinitionBuiltHistory> {
    const { flowDefinitionId, ...rest } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowDefinitionId}/diff`);
    return await this.common.postJSON(url, rest);
  }

  async findLatestTestRun(
    flowDefinitionId: string,
    dataVariant?: FlowRunBlockDataVariant
  ): Promise<{ latestRun: FlowRunWithData | null }> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${flowDefinitionId}/latest-test-run`,
      {
        dataVariant,
      }
    );
    return await this.common.getJSON(url);
  }

  async findSubFlowParents(args: {
    flowDefinitionId: string;
    limit?: number;
    offset?: number;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
  }): Promise<FlowDefinitionWithFlow[]> {
    const { flowDefinitionId, ...query } = args;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${flowDefinitionId}/subflow-parents`,
      query
    );
    return await this.common.getJSON(url);
  }

  async findSdkCompletions(): Promise<CodeMirrorCompletionSnippet[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/sdk-completions`);
    return await this.common.getJSON(url);
  }

  async applyRenameOnFlowData(args: {
    flowData: FlowData;
    blockId: string;
    oldBlockName: string;
  }): Promise<FlowData> {
    const { flowData, blockId, oldBlockName } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/block-rename`);
    return await this.common.postJSON(url, {
      flowData,
      blockId,
      oldBlockName,
    });
  }

  async findAllTags(args: { recordId: string; kind: FlowKind }): Promise<FlowTag[]> {
    const { recordId, kind } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${recordId}/tags`, { kind });
    return await this.common.getJSON(url);
  }

  async findAllTagsInUse(args: { kind: FlowKind }): Promise<FlowTag[]> {
    const { kind } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/tags/all-in-use`, { kind });
    return await this.common.getJSON(url);
  }
}
